import { forwardRef, ReactNode } from 'react';
import { Box, useMantineTheme } from '@mantine/core';
import { GridPosition, BreakpointKey } from '../types';
import { getBreakpointConfig } from './breakpoints';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// GridCell Component
// ============================================================================

export interface GridCellProps {
  /** Grid position and dimensions */
  position: GridPosition;
  /** Current breakpoint */
  breakpoint: BreakpointKey;
  /** Whether this cell is being dragged over */
  isDragOver?: boolean;
  /** Whether this cell is currently being dragged */
  isDragging?: boolean;
  /** Whether edit mode is active */
  isEditMode?: boolean;
  /** Child content (widget wrapper) */
  children: ReactNode;
  /** Optional click handler */
  onClick?: () => void;
}

export const GridCell = forwardRef<HTMLDivElement, GridCellProps>(
  (
    {
      position,
      breakpoint,
      isDragOver = false,
      isDragging = false,
      isEditMode = false,
      children,
      onClick,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const breakpointConfig = getBreakpointConfig(breakpoint);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Calculate grid position styles (CSS Grid uses 1-based indexing)
    const gridColumnStart = position.col + 1;
    const gridColumnEnd = gridColumnStart + position.width;
    const gridRowStart = position.row + 1;
    const gridRowEnd = gridRowStart + position.height;

    // Calculate min height based on rows (in pixels)
    const minHeightPx =
      position.height * breakpointConfig.rowHeight +
      (position.height - 1) * breakpointConfig.gap;

    return (
      <Box
        ref={ref}
        onClick={onClick}
        style={{
          gridColumn: `${gridColumnStart} / ${gridColumnEnd}`,
          gridRow: `${gridRowStart} / ${gridRowEnd}`,
          minHeight: `${minHeightPx}px`,
          overflow: 'hidden',
          opacity: isDragging ? 0.5 : 1,
          transform: isDragging ? 'scale(1.02)' : undefined,
          transition: isDragging ? 'all 0.2s ease' : undefined,
          zIndex: isDragging ? 100 : 'auto',
          position: 'relative',
        }}
        data-grid-col={position.col}
        data-grid-row={position.row}
        data-grid-width={position.width}
        data-grid-height={position.height}
      >
        {/* Drag over overlay */}
        {isDragOver && (
          <Box
            style={{
              position: 'absolute',
              inset: -2,
              backgroundColor: theme.colors.blue[1],
              opacity: 0.3,
              borderRadius: theme.radius.md,
              border: `2px dashed ${theme.colors.blue[5]}`,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        )}

        {/* Widget content */}
        <Box style={{ height: '100%', position: 'relative', zIndex: 1 }}>
          {children}
        </Box>
      </Box>
    );
  }
);

GridCell.displayName = 'GridCell';

// ============================================================================
// EmptyGridCell Component (for drop targets)
// ============================================================================

export interface EmptyGridCellProps {
  /** Starting column */
  col: number;
  /** Starting row */
  row: number;
  /** Width in columns */
  width?: number;
  /** Height in rows */
  height?: number;
  /** Current breakpoint */
  breakpoint: BreakpointKey;
  /** Whether this is a valid drop target */
  isDropTarget?: boolean;
  /** Whether something is being dragged over */
  isDragOver?: boolean;
  /** Drop handler */
  onDrop?: () => void;
  /** Drag over handler */
  onDragOver?: (e: React.DragEvent) => void;
  /** Drag leave handler */
  onDragLeave?: () => void;
}

export function EmptyGridCell({
  col,
  row,
  width = 1,
  height = 1,
  breakpoint,
  isDropTarget = false,
  isDragOver = false,
  onDrop,
  onDragOver,
  onDragLeave,
}: EmptyGridCellProps) {
  const theme = useMantineTheme();
  const breakpointConfig = getBreakpointConfig(breakpoint);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStylesForEmptyCell = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const gridColumnStart = col + 1;
  const gridColumnEnd = gridColumnStart + width;
  const gridRowStart = row + 1;
  const gridRowEnd = gridRowStart + height;

  const minHeightPx =
    height * breakpointConfig.rowHeight + (height - 1) * breakpointConfig.gap;

  if (!isDropTarget) return null;

  return (
    <Box
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      style={{
        gridColumn: `${gridColumnStart} / ${gridColumnEnd}`,
        gridRow: `${gridRowStart} / ${gridRowEnd}`,
        minHeight: `${minHeightPx}px`,
        border: `2px dashed ${isDragOver ? theme.colors.blue[5] : theme.colors.gray[3]}`,
        borderRadius: theme.radius.md,
        backgroundColor: isDragOver ? theme.colors.blue[0] : 'transparent',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isDragOver && (
        <Box
          style={{
            color: theme.colors.blue[5],
            fontSize: theme.fontSizes.sm,
            fontWeight: 500,
            ...accessibilityStylesForEmptyCell,
          }}
        >
          Drop here
        </Box>
      )}
    </Box>
  );
}

export default GridCell;
